/**
 * Trend computation for the check-in digest.
 *
 * The training sources are explicit that trends matter and individual sessions do
 * not, so this module deliberately produces directions and averages rather than
 * bests. There is no "personal record" concept here on purpose: given the failure
 * mode is hypoxic blackout, a UI that rewards a bigger number is actively unsafe.
 *
 * Everything is a pure function over session history so it can be tested and so
 * the digest handed to Nereus is reproducible.
 */

import {
  ApiSessionType,
  ApneaSessionDto,
  toRuleSessionType,
} from '../types/Apnea';
import { BOLT_DECLINE_DAYS, TABLE_SESSION_TYPES } from './apneaRules';

export type TrendDirection = 'improving' | 'steady' | 'declining' | 'insufficient';

export interface Trend {
  direction: TrendDirection;
  /** Mean of the earlier half, then the later half. Null when there is too little data. */
  earlier: number | null;
  later: number | null;
  sampleCount: number;
}

const mean = (values: number[]): number | null =>
  values.length === 0 ? null : values.reduce((a, b) => a + b, 0) / values.length;

/**
 * Split a series in half and compare the means. A deliberately blunt method:
 * with a handful of subjective 1-5 ratings, regression would imply more precision
 * than the data carries.
 */
export const computeTrend = (values: number[], minimumSamples = 4): Trend => {
  if (values.length < minimumSamples) {
    return { direction: 'insufficient', earlier: null, later: null, sampleCount: values.length };
  }
  const mid = Math.floor(values.length / 2);
  const earlier = mean(values.slice(0, mid))!;
  const later = mean(values.slice(mid))!;
  const delta = later - earlier;
  // 0.4 on a 1-5 scale: smaller moves are noise in self-reported ratings.
  const direction: TrendDirection = Math.abs(delta) < 0.4
    ? 'steady'
    : delta > 0 ? 'improving' : 'declining';
  return { direction, earlier, later, sampleCount: values.length };
};

const chronological = (sessions: ApneaSessionDto[]): ApneaSessionDto[] =>
  [...sessions].sort(
    (a, b) => new Date(a.startedAt).getTime() - new Date(b.startedAt).getTime()
  );

export interface AdherenceSummary {
  sessionsCompleted: number;
  tableSessions: number;
  abortedSessions: number;
  /** Distinct days trained, which reads more honestly than a raw session count. */
  daysTrained: number;
  windowDays: number;
}

export const computeAdherence = (
  sessions: ApneaSessionDto[],
  windowDays: number
): AdherenceSummary => {
  const days = new Set(sessions.map(s => new Date(s.startedAt).toDateString()));
  return {
    sessionsCompleted: sessions.length,
    tableSessions: sessions.filter(
      s => TABLE_SESSION_TYPES.includes(toRuleSessionType(s.type))
    ).length,
    abortedSessions: sessions.filter(s => s.aborted).length,
    daysTrained: days.size,
    windowDays,
  };
};

export interface ContractionSummary {
  trend: Trend;
  /** Mean first-contraction time, in seconds. The key adaptation signal. */
  averageSeconds: number | null;
}

/**
 * First-contraction timing. Later contractions across weeks is the progress
 * indicator both sources name, and unlike hold duration it cannot be gamed by
 * pushing into hypoxia.
 */
export const computeContractionTrend = (sessions: ApneaSessionDto[]): ContractionSummary => {
  const marks = chronological(sessions)
    .flatMap(s => s.actualSets
      .map(set => set.firstContractionAt)
      .filter((v): v is number => typeof v === 'number'));
  return {
    trend: computeTrend(marks),
    averageSeconds: mean(marks),
  };
};

export interface BoltSummary {
  latest: number | null;
  trend: Trend;
  /** Consecutive declining readings at the end of the series. */
  decliningStreak: number;
  /** True when the streak has reached the documented back-off threshold. */
  backOffAdvised: boolean;
  /** How many readings were hand-entered, which is weaker evidence. */
  manualCount: number;
}

export const computeBoltSummary = (sessions: ApneaSessionDto[]): BoltSummary => {
  const readings = chronological(sessions)
    .filter(s => s.bolt)
    .map(s => ({ seconds: s.bolt!.seconds, manual: s.bolt!.source !== 'Timed' }));

  let decliningStreak = 0;
  for (let i = readings.length - 1; i > 0; i -= 1) {
    if (readings[i].seconds < readings[i - 1].seconds) decliningStreak += 1;
    else break;
  }

  return {
    latest: readings.length > 0 ? readings[readings.length - 1].seconds : null,
    trend: computeTrend(readings.map(r => r.seconds)),
    decliningStreak,
    backOffAdvised: decliningStreak >= BOLT_DECLINE_DAYS,
    manualCount: readings.filter(r => r.manual).length,
  };
};

export interface MaxStaticSummary {
  latestSeconds: number | null;
  latestAt: string | null;
  attempts: number;
  trend: Trend;
}

export const computeMaxStaticSummary = (sessions: ApneaSessionDto[]): MaxStaticSummary => {
  const attempts = chronological(sessions).filter(s => s.maxTest);
  const latest = attempts[attempts.length - 1];
  return {
    latestSeconds: latest?.maxTest?.seconds ?? null,
    latestAt: latest?.startedAt ?? null,
    attempts: attempts.length,
    trend: computeTrend(attempts.map(a => a.maxTest!.seconds), 3),
  };
};

export interface WalkSummary {
  sessions: number;
  /** Mean distance per rep, in metres. */
  averageDistanceMeters: number | null;
  trend: Trend;
}

export const computeWalkSummary = (sessions: ApneaSessionDto[]): WalkSummary => {
  const walks = chronological(sessions).filter(s => s.walk && s.walk.reps.length > 0);
  const perSession = walks
    .map(s => mean(
      s.walk!.reps
        .map(r => r.distanceMeters)
        .filter((v): v is number => typeof v === 'number')
    ))
    .filter((v): v is number => v !== null);

  return {
    sessions: walks.length,
    averageDistanceMeters: mean(perSession),
    trend: computeTrend(perSession, 3),
  };
};

export interface WarningSummary {
  count: number;
  hasSeriousEvent: boolean;
  mostRecentAt: string | null;
}

export const computeWarnings = (sessions: ApneaSessionDto[]): WarningSummary => {
  const flagged = chronological(sessions).filter(s => s.warningSigns?.occurred);
  const serious = flagged.filter(
    s => s.warningSigns.kind === 'Lmc' || s.warningSigns.kind === 'Blackout'
  );
  return {
    count: flagged.length,
    hasSeriousEvent: serious.length > 0,
    mostRecentAt: flagged.length > 0 ? flagged[flagged.length - 1].startedAt : null,
  };
};

/**
 * The full digest handed to the check-in. Kept compact and numeric so it can be
 * summarised for Nereus without dumping raw session history into a prompt.
 */
export interface CheckInDigest {
  windowDays: number;
  adherence: AdherenceSummary;
  recovery: Trend;
  relaxation: Trend;
  technique: Trend;
  effort: Trend;
  contractions: ContractionSummary;
  bolt: BoltSummary;
  maxStatic: MaxStaticSummary;
  walks: WalkSummary;
  warnings: WarningSummary;
  /** Session types trained in the window, for spotting a neglected modality. */
  typesTrained: ApiSessionType[];
}

const metricSeries = (
  sessions: ApneaSessionDto[],
  key: 'recovery' | 'relaxation' | 'technique' | 'perceivedEffort'
): number[] => chronological(sessions)
  .map(s => s.metrics?.[key])
  .filter((v): v is number => typeof v === 'number');

export const buildCheckInDigest = (
  sessions: ApneaSessionDto[],
  windowDays = 14
): CheckInDigest => ({
  windowDays,
  adherence: computeAdherence(sessions, windowDays),
  recovery: computeTrend(metricSeries(sessions, 'recovery')),
  relaxation: computeTrend(metricSeries(sessions, 'relaxation')),
  technique: computeTrend(metricSeries(sessions, 'technique')),
  effort: computeTrend(metricSeries(sessions, 'perceivedEffort')),
  contractions: computeContractionTrend(sessions),
  bolt: computeBoltSummary(sessions),
  maxStatic: computeMaxStaticSummary(sessions),
  walks: computeWalkSummary(sessions),
  warnings: computeWarnings(sessions),
  typesTrained: Array.from(new Set(sessions.map(s => s.type))),
});

/**
 * Whether the evidence supports progressing. Deliberately conservative: the
 * sources require the current level to be calm, controlled and repeatable first,
 * and treat "repeat" as the normal answer rather than a failure.
 */
export interface ProgressionReadiness {
  recommendation: 'progress' | 'repeat' | 'reduce';
  reasons: string[];
}

export const assessProgression = (digest: CheckInDigest): ProgressionReadiness => {
  const reasons: string[] = [];

  if (digest.warnings.hasSeriousEvent) {
    return {
      recommendation: 'reduce',
      reasons: ['A loss of motor control or blackout was logged. Resume at a clearly easier level.'],
    };
  }

  if (digest.recovery.direction === 'declining') {
    reasons.push('Recovery quality is trending down.');
  }
  if (digest.bolt.backOffAdvised) {
    reasons.push(`BOLT has fallen ${digest.bolt.decliningStreak} readings running.`);
  }
  if (digest.warnings.count > 0) {
    reasons.push('Warning signs were recorded in this block.');
  }
  if (reasons.length > 0) {
    return { recommendation: 'reduce', reasons };
  }

  if (digest.adherence.tableSessions < 2) {
    return {
      recommendation: 'repeat',
      reasons: ['Not enough table work in this block to judge whether the level is repeatable.'],
    };
  }
  if (digest.recovery.direction === 'insufficient') {
    return {
      recommendation: 'repeat',
      reasons: ['Not enough rated sessions yet to see a recovery trend.'],
    };
  }

  const supportive: string[] = [];
  if (digest.recovery.direction === 'improving') supportive.push('Recovery is improving.');
  if (digest.recovery.direction === 'steady') supportive.push('Recovery is holding steady.');
  if (digest.contractions.trend.direction === 'improving') {
    supportive.push('Contractions are arriving later.');
  }
  if (digest.relaxation.direction === 'improving') supportive.push('Relaxation is improving.');

  // Progress only on positive evidence, never merely on the absence of problems.
  if (supportive.length >= 2 && digest.adherence.abortedSessions === 0) {
    return { recommendation: 'progress', reasons: supportive };
  }

  return {
    recommendation: 'repeat',
    reasons: supportive.length > 0
      ? [...supportive, 'Not yet enough signal to justify progressing.']
      : ['The current level does not yet look calm and repeatable.'],
  };
};

/** Human-readable digest for the check-in conversation and the Plan tab. */
export const summariseDigest = (digest: CheckInDigest): string[] => {
  const lines: string[] = [];
  const { adherence, recovery, contractions, bolt } = digest;

  lines.push(
    `${adherence.sessionsCompleted} sessions over ${adherence.daysTrained} days `
    + `(${adherence.tableSessions} table sessions) in the last ${digest.windowDays} days.`
  );

  if (recovery.direction !== 'insufficient') {
    lines.push(`Recovery is ${recovery.direction}.`);
  }
  if (contractions.averageSeconds !== null) {
    lines.push(
      `First contraction averaging ${Math.round(contractions.averageSeconds)}s`
      + (contractions.trend.direction === 'improving' ? ' and arriving later.' : '.')
    );
  }
  if (bolt.latest !== null) {
    lines.push(
      `Latest BOLT ${bolt.latest}s`
      + (bolt.backOffAdvised ? ` — down ${bolt.decliningStreak} readings, back off.` : '.')
    );
  }
  if (digest.warnings.count > 0) {
    lines.push(`${digest.warnings.count} session(s) recorded warning signs.`);
  }
  if (adherence.abortedSessions > 0) {
    lines.push(`${adherence.abortedSessions} session(s) ended early.`);
  }

  return lines;
};
